import React from "react";
import {
  Box,
  Typography,
  Paper,
  Icon,
  Chip,
  alpha,
  Skeleton,
  Button,
  Pagination,
} from "@mui/material";
import { ActivityLog } from "../../services/purchaseRequest.service";

interface ActivityTimelineProps {
  activities: ActivityLog[];
  loading?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  showPagination?: boolean;
}

// Activity configuration for icons, colors, and labels
const activityConfig: Record<
  string,
  {
    icon: string;
    color: string;
    bgColor: string;
    label: string;
    description: string;
  }
> = {
  created: {
    icon: "add_circle",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    label: "Created",
    description: "Purchase request was created",
  },
  updated: {
    icon: "edit",
    color: "#0891B2",
    bgColor: "#ECFEFF",
    label: "Updated",
    description: "Purchase request was modified",
  },
  submitted: {
    icon: "send",
    color: "#7C3AED",
    bgColor: "#F3E8FF",
    label: "Submitted",
    description: "Purchase request was submitted for approval",
  },
  approved: {
    icon: "check_circle",
    color: "#059669",
    bgColor: "#ECFDF5",
    label: "Approved",
    description: "Purchase request was approved",
  },
  rejected: {
    icon: "cancel",
    color: "#DC2626",
    bgColor: "#FEF2F2",
    label: "Rejected",
    description: "Purchase request was rejected",
  },
  reopened: {
    icon: "refresh",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    label: "Reopened",
    description: "Purchase request was reopened for editing",
  },
};

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor(diffInHours * 60);
    return diffInMinutes <= 1 ? "Just now" : `${diffInMinutes} minutes ago`;
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (diffInHours < 168) {
    // 7 days
    const days = Math.floor(diffInHours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
};

const ActivityTimelineItem: React.FC<{
  activity: ActivityLog;
  isLast: boolean;
}> = ({ activity, isLast }) => {
  const config = activityConfig[activity.action] || activityConfig.updated;

  return (
    <Box sx={{ position: "relative", pb: isLast ? 0 : 3 }}>
      {/* Timeline line */}
      {!isLast && (
        <Box
          sx={{
            position: "absolute",
            left: 20,
            top: 48,
            bottom: 0,
            width: 2,
            bgcolor: "#E5E7EB",
            zIndex: 0,
          }}
        />
      )}

      <Box sx={{ display: "flex", gap: 2, position: "relative", zIndex: 1 }}>
        {/* Timeline icon */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: config.bgColor,
            color: config.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${config.color}`,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20 }}>{config.icon}</Icon>
        </Box>

        {/* Activity content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: `1px solid ${alpha(config.color, 0.2)}`,
              "&:hover": {
                boxShadow: 1,
                borderColor: alpha(config.color, 0.3),
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Chip
                  label={config.label}
                  size="small"
                  sx={{
                    bgcolor: config.bgColor,
                    color: config.color,
                    fontWeight: 600,
                    border: `1px solid ${alpha(config.color, 0.3)}`,
                  }}
                />
                {activity.previous_status && activity.new_status && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {activity.previous_status}
                    </Typography>
                    <Icon sx={{ fontSize: 14, color: "text.secondary" }}>
                      arrow_forward
                    </Icon>
                    <Typography variant="caption" color="text.secondary">
                      {activity.new_status}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                }}
              >
                {formatTimestamp(activity.timestamp)}
              </Typography>
            </Box>

            {/* Details */}
            {activity.details && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.primary",
                  mb: activity.rejection_reason || activity.notes ? 1 : 0,
                }}
              >
                {activity.details}
              </Typography>
            )}

            {/* Rejection reason */}
            {activity.rejection_reason && (
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: alpha("#DC2626", 0.05),
                  border: `1px solid ${alpha("#DC2626", 0.2)}`,
                  borderRadius: 1,
                  mb: activity.notes ? 1 : 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#DC2626",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Rejection Reason:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#DC2626",
                    fontStyle: "italic",
                  }}
                >
                  "{activity.rejection_reason}"
                </Typography>
              </Paper>
            )}

            {/* Notes */}
            {activity.notes && (
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: alpha("#3B82F6", 0.05),
                  border: `1px solid ${alpha("#3B82F6", 0.2)}`,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#3B82F6",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Notes:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#3B82F6",
                  }}
                >
                  {activity.notes}
                </Typography>
              </Paper>
            )}

            {/* Changed by */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Icon sx={{ fontSize: 16, color: "text.secondary" }}>person</Icon>
              <Typography variant="caption" color="text.secondary">
                by {activity.changed_by_email || activity.changed_by}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

const ActivityTimelineSkeleton: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
    {[1, 2, 3].map((index) => (
      <Box key={index} sx={{ display: "flex", gap: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton
            variant="rectangular"
            height={120}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </Box>
    ))}
  </Box>
);

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  activities,
  loading = false,
  total = 0,
  page = 1,
  pageSize = 10,
  onPageChange,
  showPagination = true,
}) => {
  if (loading) {
    return <ActivityTimelineSkeleton />;
  }

  if (activities.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 6,
          color: "text.secondary",
        }}
      >
        <Icon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}>history</Icon>
        <Typography variant="h6" sx={{ mb: 1, opacity: 0.7 }}>
          No Activity Yet
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.6 }}>
          Activity history will appear here as actions are performed on this
          request.
        </Typography>
      </Box>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Box>
      <Box sx={{ mb: showPagination && totalPages > 1 ? 3 : 0 }}>
        {activities.map((activity, index) => (
          <ActivityTimelineItem
            key={`${activity.timestamp}-${activity.action}-${index}`}
            activity={activity}
            isLast={index === activities.length - 1}
          />
        ))}
      </Box>

      {showPagination && totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange?.(newPage)}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};
